import React from "react";
import { Space } from "antd";
import { AxPagination, AxDivider, AxButton } from "@dynata/axiom3";

class Pagination extends React.Component {
  render() {
    return (
      <div className="section" id="pagination">
        <h3>Pagination</h3>
        <p>
          A long list can be divided into several pages using Pagination, and
          only one page will be loaded at a time.
        </p>
        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>
        <Space direction="vertical">
          <Space size="large" direction="vertical">
            <pre>{`<AxPagination/>`}</pre>
            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/pagination/#API"
                >
                  https://ant.design/components/pagination/#API
                </AxButton>
              </p>
            </div>

            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>size</td>
                  <td>"default | small"</td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td>current</td>
                  <td>Current page number</td>
                  <td>number</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultCurrent</td>
                  <td>Default initial page number</td>
                  <td>number</td>
                  <td>1</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultPageSize</td>
                  <td>Default number of data items per page</td>
                  <td>number</td>
                  <td>10</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Disable pagination</td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>hideOnSinglePage</td>
                  <td>Whether to hide pager on single page</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>itemRender</td>
                  <td>To customize item's innerHTML</td>
                  <td>
                    (page, type: 'page' | 'prev' | 'next', originalElement)
                    =&gt; React.ReactNode
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>pageSize</td>
                  <td>Number of data items per page</td>
                  <td>number</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>pageSizeOptions</td>
                  <td>Specify the sizeChanger options</td>
                  <td>string[]</td>
                  <td>
                    [<code>10</code>, <code>20</code>, <code>50</code>,{" "}
                    <code>100</code>]
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>responsive</td>
                  <td>
                    If <code>size</code> is not specified,{" "}
                    <code>Pagination</code> would resize according to the width
                    of the window
                  </td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showLessItems</td>
                  <td>Show less page items</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showQuickJumper</td>
                  <td>Determine whether you can jump to pages directly</td>
                  <td>
                    boolean | <pre>{`{ goButton: ReactNode }`}</pre>
                  </td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showSizeChanger</td>
                  <td>
                    Determine whether to show <code>pageSize</code> select, it
                    will be true when <code>total &gt; 50</code>
                  </td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showTitle</td>
                  <td>Show page item's title</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showTotal</td>
                  <td>To display the total number and range</td>
                  <td>function(total, range)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>simple</td>
                  <td>Whether to use simple mode</td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>size</td>
                  <td>
                    Specify the size of <code>Pagination</code>, can be set to{" "}
                    <code>small</code>
                  </td>
                  <td>
                    <code>default</code> | <code>small</code>
                  </td>
                  <td>
                    <code>default</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>total</td>
                  <td>Total number of data items</td>
                  <td>number</td>
                  <td>0</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onChange</td>
                  <td>
                    Called when the page number or <code>pageSize</code> is
                    changed, and it takes the resulting page number and pageSize
                    as its arguments
                  </td>
                  <td>function(page, pageSize)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onShowSizeChange</td>
                  <td>
                    Called when <code>pageSize</code> is changed
                  </td>
                  <td>function(current, size)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/pagination/#API"
                      >
                        {`<Pagination />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            {/* <pre>{`<AxPagination total={85} showTotal={(total) => `Total ${total} items`} />`}</pre> */}
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Default | Small
        </AxDivider>

        <Space direction="vertical" size={40}>
          <pre>
            {`
<AxPagination
  total={85}
  showTotal={(total) => \`Total \${total} items\`}
/>
<AxPagination
  size="small"
  total={50}
  showTotal={(total) => \`Total \${total} items\`}
/>            
`}
          </pre>
          <AxPagination
            total={85}
            showTotal={(total) => `Total ${total} items`}
          />
          <AxPagination
            size="small"
            total={50}
            showTotal={(total) => `Total ${total} items`}
          />
        </Space>
      </div>
    );
  }
}
export default Pagination;
